import { useEffect, useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "./Logo";

const NAV = [
  { label: "Home", to: "/" },
  { label: "What We Do", to: "/what-we-do" },
  { label: "Process", to: "/", hash: "process" },
  { label: "Pricing", to: "/pricing" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, item) => {
    setOpen(false);
    if (item.hash) {
      e.preventDefault();
      if (location.pathname === "/") {
        document.getElementById(item.hash)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/", { state: { scrollTo: item.hash } });
      }
    }
  };

  const linkClass = ({ isActive }) =>
    `text-[15px] font-semibold transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:bg-gradient-to-r after:from-[#00A8FF] after:to-[#39D5FF] after:transition-all after:duration-300 ${
      isActive ? "text-[#F7FAFF] after:w-full" : "text-[#AEBFD8] hover:text-[#F7FAFF] after:w-0 hover:after:w-full"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#020B1D]/80 backdrop-blur-xl shadow-[0_4px_32px_rgba(0,168,255,0.12)] border-b border-[rgba(67,157,255,0.2)]"
          : "bg-[#020B1D]/30 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="shrink-0" data-testid="header-logo-link">
          <Logo size={36} wordmarkClass="text-lg md:text-xl" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {NAV.map((n) =>
            n.hash ? (
              <a key={n.label} href={`/#${n.hash}`} onClick={(e) => handleClick(e, n)}
                data-testid={`nav-link-${n.hash}`} className={linkClass({ isActive: false })}>
                {n.label}
              </a>
            ) : (
              <NavLink key={n.label} to={n.to} end={n.to === "/"}
                data-testid={`nav-link-${n.to === "/" ? "home" : n.to.slice(1)}`} className={linkClass}>
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="header-cta"
            className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold min-h-[44px]">
            Get a Free Consultation <ArrowRight size={16} />
          </Link>
        </div>

        <button
          className="lg:hidden text-[#F7FAFF] p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#020B1D]/95 backdrop-blur-2xl border-t border-[rgba(67,157,255,0.2)] px-5 py-6" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1" aria-label="Mobile">
            {NAV.map((n) =>
              n.hash ? (
                <a key={n.label} href={`/#${n.hash}`} onClick={(e) => handleClick(e, n)}
                  data-testid={`mobile-nav-link-${n.hash}`}
                  className="text-base font-semibold text-[#AEBFD8] hover:text-[#F7FAFF] py-3 px-2 rounded-lg hover:bg-white/5 transition min-h-[44px] flex items-center">
                  {n.label}
                </a>
              ) : (
                <NavLink key={n.label} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${n.to === "/" ? "home" : n.to.slice(1)}`}
                  className="text-base font-semibold text-[#AEBFD8] hover:text-[#F7FAFF] py-3 px-2 rounded-lg hover:bg-white/5 transition min-h-[44px] flex items-center">
                  {n.label}
                </NavLink>
              )
            )}
            <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-header-cta"
              className="btn-primary inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold mt-3 min-h-[44px]">
              Get a Free Consultation <ArrowRight size={16} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
